export function toFiniteNumber(value, fallback = null) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function round(value, digits = 6) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const m = 10 ** digits;
  return Math.round(n * m) / m;
}

const PRESSURE_TEMPLATE_PROFILES = [
  {
    key: 'cph6000',
    category: 'pressure',
    categoryLabel: 'Presión',
    label: 'CPH 6000 r14',
    displayName: 'Calibracion de Manometros - CPH 6000',
    summary: 'Usa la sesion de presion con grilla de setpoints.',
    workflow: 'pressure_session',
    templateCode: 'PRESSURE_CPH6000_R14',
    templateRevision: 'r14',
    patternReferenceName: 'Calibrador Portatil de Presion WIKA CPH6000',
    defaultSetpoints: [2.11, 4.17, 5.17, 6.18, 8.17, 10.11],
    pattern_hysteresis: 0.02,
    pattern_uncertainty_max: 0.04,
    pattern_resolution: 0.02,
    pattern_drift: 0,
    height_difference_m: 0.1,
  },
  {
    key: 'cph6600',
    category: 'pressure',
    categoryLabel: 'Presión',
    label: 'CPH 6600 r09',
    displayName: 'Calibracion de Manometros - CPH 6600',
    summary: 'Usa la sesion de presion con grilla de setpoints.',
    workflow: 'pressure_session',
    templateCode: 'PRESSURE_CPH6600_R09',
    templateRevision: 'r09',
    patternReferenceName: 'Calibrador con transmisor de presion interno WIKA CPH6600',
    defaultSetpoints: [2, 6, 8, 12, 16, 20],
    pattern_hysteresis: 0.004,
    pattern_uncertainty_max: 0.003,
    pattern_resolution: 0.001,
    pattern_drift: 0.004,
    height_difference_m: 0.1,
  },
  {
    key: 'presostato_danfoss',
    category: 'pressure',
    categoryLabel: 'Presión',
    label: 'Presostato F',
    displayName: 'Calibracion de Presostato',
    summary: 'Abre el editor de calibracion con bloque de presion con histéresis.',
    workflow: 'calibration_editor',
    templateCode: 'PRESSURE_PRESOSTATO_DANFOSS_F',
    templateRevision: 'F',
    patternReferenceName: 'Calibrador Portatil de Presion WIKA CPH6000',
    defaultSetpoints: [],
    defaultBlockType: 'pressure_hysteresis',
    pattern_hysteresis: 0.02,
    pattern_uncertainty_max: 0.04,
    pattern_resolution: 0.02,
    pattern_drift: 0,
    height_difference_m: 0.1,
  },
];

const PRESSURE_TEMPLATE_PROFILE_BY_CODE = new Map(
  PRESSURE_TEMPLATE_PROFILES.map((profile) => [profile.templateCode, profile])
);

export function resolvePressureTemplateProfile({ instrumentLrl, instrumentUrl } = {}) {
  const lrl = Math.abs(toFiniteNumber(instrumentLrl, 0));
  const url = Math.abs(toFiniteNumber(instrumentUrl, 0));
  const maxRange = Math.max(lrl, url);

  // Default split:
  // - CPH 6600 for ranges up to 20 bar
  // - CPH 6000 for ranges above 20 bar
  // Some ranges between 10 and 20 bar may still use CPH 6000 manually later.
  if (maxRange > 20) return PRESSURE_TEMPLATE_PROFILES[0];
  return PRESSURE_TEMPLATE_PROFILES[1];
}

export function resolvePressureTemplateProfileByCode(templateCode) {
  if (!templateCode) return null;
  return PRESSURE_TEMPLATE_PROFILE_BY_CODE.get(String(templateCode)) || null;
}

export function getPressureTemplateProfiles() {
  return PRESSURE_TEMPLATE_PROFILES.map((profile) => ({ ...profile }));
}

export function resolvePatternDefaultsByRange({ instrumentLrl, instrumentUrl }) {
  const profile = resolvePressureTemplateProfile({ instrumentLrl, instrumentUrl });

  return {
    pattern_hysteresis: profile.pattern_hysteresis,
    pattern_uncertainty_max: profile.pattern_uncertainty_max,
    pattern_resolution: profile.pattern_resolution,
    pattern_drift: profile.pattern_drift,
    height_difference_m: profile.height_difference_m,
  };
}

export function getPressureTemplateSetpoints({ instrumentLrl, instrumentUrl } = {}) {
  const profile = resolvePressureTemplateProfile({ instrumentLrl, instrumentUrl });
  return [...profile.defaultSetpoints];
}

export function buildLinearSetpoints({ instrumentLrl, instrumentUrl, points = 6 }) {
  const lrl = toFiniteNumber(instrumentLrl, 0);
  const url = toFiniteNumber(instrumentUrl, null);
  const total = Number.isFinite(points) && points > 1 ? Math.floor(points) : 6;

  if (!Number.isFinite(url) || url === lrl) {
    return Array.from({ length: total }, (_, i) => round(lrl + i, 6));
  }

  const step = (url - lrl) / (total - 1);
  return Array.from({ length: total }, (_, i) => round(lrl + step * i, 6));
}

export function hasAnySetpoint(rows = []) {
  return (rows || []).some((r) => Number.isFinite(Number(r?.setpoint)));
}

export function arePatternParamsEmpty(session) {
  if (!session) return true;
  const values = [
    session.pattern_hysteresis,
    session.pattern_uncertainty_max,
    session.pattern_resolution,
    session.pattern_drift,
  ];
  return values.every((v) => !Number.isFinite(Number(v)) || Number(v) === 0);
}
